
import { useState, useEffect } from "react"
import { GripVertical } from "lucide-react" // Removed Trash2 import
import CustomCheckbox from "./CustomCheckbox" // Assuming CustomCheckbox exists
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { CSS } from "@dnd-kit/utilities"
import Modal from "./Modal"

// Normalise ids generated client-side (`tmpId`) and those returned by the API (`id`).
const getItemKey = (item) => item?.id ?? item?.tmpId ?? item?.tmpid ?? item?._id;

// Sortable row component
const SortableRow = ({
  item,
  columns,
  editMode,
  editData,
  setEditData,
  onSave,
  onCancel,
  onDelete,
  onEdit,
  index,
  onShowDeleteModal,
  renderCell,
  firstRowEditable,
  renderActions, 
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: getItemKey(item),
    disabled: !editMode,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [getItemKey(item)]: {
        ...prev[getItemKey(item)],
        [field]: value,
      },
    }))
  }

  // Determine row background color based on index (even/odd)
  const rowBgClass = index % 2 === 0 ? "bg-tableau-bg1" : "bg-tableau-bg"

  // Add a subtle left border when in edit mode to indicate the row is being edited
  const editIndicatorClass = editMode ? "border-l-4 border-blue-400" : ""

  // Check if this row is editable (either it's not the first row or firstRowEditable is true)
  const isRowEditable = firstRowEditable || index !== 0

  return (
    <tr ref={setNodeRef} style={style} className={`${rowBgClass} ${editIndicatorClass}`}>
      {/* Drag handle - only visible in edit mode */}
      <td className="py-3 px-2 w-10">
        {editMode && isRowEditable && (
          <div {...attributes} {...listeners} className="cursor-grab">
            <GripVertical className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </td>

      {/* Render cells based on columns */}
      {columns.map((column) => (
        <td key={column.key} className={`py-3 px-4 ${column.className || ""}`}>
          {editMode && isRowEditable ? (
            renderCell ? (
              renderCell(
                column.key,
                item,
                editData[getItemKey(item)]?.[column.key],
                (value) => handleInputChange(column.key, value),
                true,
              )
            ) : column.type === "checkbox" ? (
              <div className="flex justify-center">
                <CustomCheckbox
                  checked={editData[item.id]?.[column.key] || false}
                  onChange={(isChecked) => handleInputChange(column.key, isChecked)}
                />
              </div>
            ) : (
              <input
                type="text"
                value={editData[item.id]?.[column.key] || ""}
                onChange={(e) => handleInputChange(column.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#5B93FF]"
              />
            )
          ) : renderCell ? (
            renderCell(column.key, item, item[column.key], null, false)
          ) : column.type === "checkbox" ? (
            <div className="flex justify-center">
              <CustomCheckbox
                checked={item[column.key] || false}
                onChange={(isChecked) => column.onChange && column.onChange(item.id, isChecked)}
                disabled={!isRowEditable}
              />
            </div>
          ) : (
            item[column.key]
          )}
        </td>
      ))}

      {/* Actions */}
      <td className="py-3 px-2 text-right">
        {isRowEditable && (
          <div className="flex space-x-2 justify-end">
            {editMode ? (
              <>
                <div className="cursor-pointer" onClick={onCancel}>
                  <img
                    src="/icons/study/Cancel.svg"
                    alt="Cancel"
                    className="h-6 w-6"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.style.display = "none"
                      e.target.parentNode.appendChild(document.createElement("span")).textContent = "X"
                    }}
                  />
                </div>
                <div className="cursor-pointer" onClick={onSave}>
                  <img
                    src="/icons/study/Save.svg"
                    alt="Save"
                    className="h-6 w-6"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.style.display = "none"
                      e.target.parentNode.appendChild(document.createElement("span")).textContent = "✓"
                    }}
                  />
                </div>
                <div className="cursor-pointer" onClick={() => onShowDeleteModal(item)}>
                  <img
                    src="/icons/study/Bin.svg"
                    alt="Bin"
                    className="h-6 w-6"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.style.display = "none"
                      e.target.parentNode.appendChild(document.createElement("span")).textContent = "🗑️"
                    }}
                  />
                </div>
              </>
            ) : renderActions ? (
              renderActions(item, onShowDeleteModal, onEdit)
            ) : (
              <>
                <button onClick={onEdit} className="text-gray-700 hover:text-gray-900 cursor-pointer" aria-label="Edit">
                  <img
                    src="/icons/study/Modifie.svg"
                    alt="Edit"
                    className="h-6 w-6"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.style.display = "none"
                      e.target.parentNode.appendChild(document.createElement("span")).textContent = "✏️"
                    }}
                  />
                </button>
                <button
                  onClick={() => onShowDeleteModal(item)}
                  className="text-gray-700 hover:text-gray-900 cursor-pointer"
                  aria-label="Delete"
                >
                  <img
                    src="/icons/study/Bin.svg"
                    alt="Bin"
                    className="h-6 w-6"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.style.display = "none"
                      e.target.parentNode.appendChild(document.createElement("span")).textContent = "🗑️"
                    }}
                  />
                </button>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

// Regular row component for non-edit mode
const RegularRow = ({
  item,
  columns,
  onEdit,
  onDelete,
  index,
  onShowDeleteModal,
  renderCell,
  firstRowEditable,
  renderActions,
}) => {
  // Determine row background color based on index (even/odd)
  const rowBgClass = index % 2 === 0 ? "bg-tableau-bg1" : "bg-tableau-bg"

  // Check if this row is editable (either it's not the first row or firstRowEditable is true)
  const isRowEditable = firstRowEditable || index !== 0

  return (
    <tr className={rowBgClass}>
      {/* Empty cell where drag handle would be */}
      <td className="py-3 px-2 w-10"></td>

      {/* Render cells based on columns */}
      {columns.map((column) => (
        <td key={column.key} className={`py-3 px-4 ${column.className || ""}`}>
          {renderCell ? (
            renderCell(column.key, item, item[column.key], null, false)
          ) : column.type === "checkbox" ? (
            <div className="flex justify-center">
              <CustomCheckbox
                checked={item[column.key] || false}
                onChange={(isChecked) => column.onChange && column.onChange(item.id, isChecked)}
                disabled={!isRowEditable}
              />
            </div>
          ) : (
            item[column.key]
          )}
        </td>
      ))}

      {/* Actions - Only show for editable rows */}
      <td className="py-3 px-4 text-right">
        {isRowEditable &&
          (renderActions ? ( // Use renderActions if provided
            renderActions(item, onShowDeleteModal, onEdit)
          ) : (
            <div className="flex space-x-2 justify-end">
              <button
                onClick={() => onEdit(item)}
                className="text-gray-700 hover:text-gray-900 cursor-pointer"
                aria-label="Edit"
              >
                <img
                  src="/icons/study/Modifie.svg"
                  alt="Edit"
                  className="h-6 w-6"
                  onError={(e) => {
                    e.target.onerror = null
                    e.target.style.display = "none"
                    e.target.parentNode.appendChild(document.createElement("span")).textContent = "✏️"
                  }}
                />
              </button>
              <button
                onClick={() => onShowDeleteModal(item)}
                className="text-gray-700 hover:text-gray-900 cursor-pointer"
                aria-label="Delete"
              >
                <img
                  src="/icons/study/Bin.svg"
                  alt="Bin"
                  className="h-6 w-6"
                  onError={(e) => {
                    e.target.onerror = null
                    e.target.style.display = "none"
                    e.target.parentNode.appendChild(document.createElement("span")).textContent = "🗑️"
                  }}
                />
              </button>
            </div>
          ))}
      </td>
    </tr>
  )
}

export default function EditableTable({
  items = [],
  columns = [],
  onEdit = null,
  onDelete = null,
  onReorder = null,
  emptyMessage = "No items have been recorded yet. They will appear in the table once added",
  saveModalTitle = "Save changes",
  saveModalMessage = "You are about to save the modifications. These changes will be applied immediately. ",
  saveModalP = "Once saved, these modifications will be applied to the study and may affect the workflow.",
  cancelModalTitle = "Discard changes",
  cancelModalMessage = "You are about to discard all modifications. This action cannot be undone.",
  deleteModalTitle = "Delete item",
  deleteModalMessage = "You are about to permanently delete this item. This action cannot be undone.",
  renderCell = null,
  firstRowEditable = true,
  renderActions = null, 
}) {
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState({})

  // Modal states
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor))

  // Keep the editable snapshot aligned with upstream changes (e.g. modal edits outside inline inputs).
  useEffect(() => {
    if (!editMode) return

    setEditData((prev) => {
      const next = { ...prev }

      items.forEach((item, index) => {
        if (firstRowEditable || index !== 0) {
          const key = getItemKey(item)
          if (!key) return
          next[key] = {
            ...next[key],
            ...item,
          }
        }
      })

      return next
    })
  }, [items, editMode, firstRowEditable])

  // Handle edit button click - enable edit mode for all items
  const handleEdit = (item) => {
    console.log("Edit clicked for item:", item)

    // Initialize edit data for all items
    const initialEditData = {}
    items.forEach((i, index) => {
      // Skip the first item (index 0) if firstRowEditable is false
      if (firstRowEditable || index !== 0) {
        initialEditData[getItemKey(i)] = { ...i }
      }
    })
    setEditData(initialEditData)
    setEditMode(true)
  }

  // Handle save button click - show confirmation modal
  const handleSaveClick = () => {
    console.log("Save clicked")
    setShowSaveModal(true)
  }

  // Handle cancel button click - show confirmation modal
  const handleCancelClick = () => {
    console.log("Cancel clicked")
    setShowCancelModal(true)
  }

  // Handle delete button click - show confirmation modal
  const handleDeleteClick = (item) => {
    console.log("Delete clicked for item:", item)
    setItemToDelete(item)
    setShowDeleteModal(true)
  }

  // Handle save confirmation
  const handleSaveConfirm = () => {
    console.log("Save confirmed")

    // Update all items with edited data, respecting firstRowEditable
    const updatedItems = items.map((item, index) => {
      if (!firstRowEditable && index === 0) {
        return item // Preserve the first item unchanged if not editable
      }
      return {
        ...item,
        ...editData[getItemKey(item)],
      }
    })

    if (onEdit) {
     
      updatedItems.forEach((item, index) => {
        if (firstRowEditable || index !== 0) {
          onEdit(item)
        }
      })
    }

    setEditMode(false)
    setShowSaveModal(false)
  }

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    console.log("Cancel confirmed")
    setEditMode(false)
    setShowCancelModal(false)
  }

  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    console.log("Delete confirmed for item:", itemToDelete)

    if (onDelete && itemToDelete) {
      onDelete(itemToDelete)
    }
    setShowDeleteModal(false)
    setItemToDelete(null)
  }

  // Handle DnD end
  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id !== over.id && over) {
      const oldIndex = items.findIndex((item) => getItemKey(item) === active.id)
      const newIndex = items.findIndex((item) => getItemKey(item) === over.id)

      if (onReorder && typeof onReorder === "function") {
        onReorder(oldIndex, newIndex)
      }
    }
  }

  // Check if data is empty
  const isEmpty = items.length === 0

  // Render table content based on edit mode
  const renderTableContent = () => {
    if (isEmpty) {
      return (
        <tr>
          <td colSpan={columns.length + 2} className="text-center py-6 bg-tableau-bg1">
            {emptyMessage}
          </td>
        </tr>
      )
    }

    if (editMode) {
      // In edit mode, use DndContext for drag and drop
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={items.map((item) => getItemKey(item))} strategy={verticalListSortingStrategy}>
            {items.map((item, index) => (
              <SortableRow
                key={item.id || item.tmpId || Math.random()}
                item={item}
                columns={columns}
                editMode={editMode}
                editData={editData}
                setEditData={setEditData}
                onSave={handleSaveClick}
                onCancel={handleCancelClick}
                onDelete={onDelete}
                onEdit={handleEdit}
                index={index}
                onShowDeleteModal={handleDeleteClick}
                renderCell={renderCell}
                firstRowEditable={firstRowEditable}
                renderActions={renderActions} 
              />
            ))}
          </SortableContext>
        </DndContext>
      )
    } else {
      // In normal mode, use regular rows without drag and drop
      return items.map((item, index) => (
        <RegularRow
          key={item.id || item.tmpId || Math.random()}
          item={item}
          columns={columns}
          onEdit={() => handleEdit(item)}
          onDelete={onDelete}
          index={index}
          onShowDeleteModal={handleDeleteClick}
          renderCell={renderCell}
          firstRowEditable={firstRowEditable}
          renderActions={renderActions} 
        />
      ))
    }
  }

  

  return (
    <div className="bg-tableau-bg rounded-xl overflow-hidden shadow-sm border border-tableau-border">
      <table className="w-full border-collapse">
        <thead className="bg-tableau-header border-b border-tableau-border">
          <tr className="text-left">
            <th className="py-3 px-2 w-10"></th>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`py-3 px-4 font-medium text-gray-700 ${column.headerClassName || ""}`}
                style={{ width: column.width || "auto" }}
              >
                {column.header}
              </th>
            ))}
            {/* Increased width for Actions column */}
            <th className="py-3 px-4 min-w-[180px] text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="text-gray-500 font-normal">{renderTableContent()}</tbody>
      </table>

      {/* Save Confirmation Modal */}
      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title={saveModalTitle}
        primaryAction={handleSaveConfirm}
        primaryActionLabel="Save Changes"
        secondaryAction={() => setShowSaveModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">{saveModalMessage}</p>
        <p className="mb-4">{saveModalP}</p>
        <p className="font-medium">Do you want to continue?</p>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title={cancelModalTitle}
        primaryAction={handleCancelConfirm}
        primaryActionLabel="Discard Changes"
        secondaryAction={() => setShowCancelModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">{cancelModalMessage}</p>
        <p className="font-medium">Are you sure you want to proceed?</p>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={deleteModalTitle}
        primaryAction={handleDeleteConfirm}
        primaryActionLabel="Delete"
        secondaryAction={() => setShowDeleteModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">{deleteModalMessage}</p>
        <p className="font-medium">Are you sure you want to proceed?</p>
      </Modal>
    </div>
  )
}
